import type { GameRenderer } from '@vpgc/render-pixi';
import { Container, Graphics, Text, type TextStyleFontWeight } from 'pixi.js';
import { CAMERA, PLANET } from '../domain/content.js';
import type { ViewModel } from '../contract.js';
import { computeScreenLayout, computeTouchControls, type Rect } from './layout.js';
import { drawStickFigure } from './vector-stick-figure.js';

const BACKGROUND = 0x111827;
const PLANET_CORE = 0x275c4d;
const PLANET_SURFACE = 0x7fd082;
const PLANET_RIM = 0xd6ef99;
const WATER = 0x5eb8d6;
const WATER_DARK = 0x277ca0;
const TREE = 0x1d5a44;
const TREE_DARK = 0x12392f;
const STONE = 0xd8cfa7;
const PANEL = 0x111a28;
const PANEL_STROKE = 0x7fd082;
const TEXT = 0xf7efd7;
const MUTED = 0x9fb7ba;
const ACCENT = 0xf0c36a;
const PLAYER_DOT = 0xfff4b6;

export class PlanetRunnerRenderer implements GameRenderer<ViewModel> {
  private root: Container | null = null;
  private readonly worldLayer = new Container();
  private readonly sky = new Graphics();
  private readonly planet = new Graphics();
  private readonly features = new Graphics();
  private readonly player = new Graphics();
  private readonly ui = new Graphics();
  private readonly hud = makeText(12, TEXT, '700');
  private readonly detail = makeText(10, MUTED, '600');
  private readonly title = makeText(13, TEXT, '800');
  private readonly statusText = makeText(12, TEXT, '700');

  mount(stage: unknown): void {
    this.root = stage as Container;
    this.worldLayer.addChild(this.sky, this.planet, this.features, this.player);
    this.root.addChild(this.worldLayer, this.ui, this.title, this.hud, this.detail, this.statusText);
  }

  draw(view: ViewModel): void {
    const screenW = Math.max(1, globalThis.innerWidth || 960);
    const screenH = Math.max(1, globalThis.innerHeight || 540);
    const layout = computeScreenLayout(screenW, screenH);

    this.worldLayer.pivot.set(view.camera.x, view.camera.y);
    this.worldLayer.position.set(screenW * 0.5, screenH * CAMERA.playerScreenY);
    this.worldLayer.scale.set(view.camera.zoom);
    this.worldLayer.rotation = view.camera.rotation;

    this.drawSky(view, screenW, screenH);
    this.drawPlanet(view);
    this.drawFeatures(view);
    this.drawPlayer(view);
    this.drawUi(view, layout);
  }

  destroy(): void {
    this.root?.removeChildren();
    this.root = null;
  }

  private drawSky(view: ViewModel, screenW: number, screenH: number): void {
    const invZoom = 1 / view.camera.zoom;
    const left = view.camera.x - screenW * 0.5 * invZoom - 80;
    const top = view.camera.y - screenH * CAMERA.playerScreenY * invZoom - 80;
    const width = screenW * invZoom + 160;
    const height = screenH * invZoom + 160;

    this.sky.clear();
    this.sky.rect(left, top, width, height).fill({ color: BACKGROUND });
  }

  private drawPlanet(view: ViewModel): void {
    const planet = view.planet;
    this.planet.clear();
    this.planet.circle(planet.x, planet.y, planet.radius + 22).fill({ color: PLANET_RIM, alpha: 0.86 });
    this.planet.circle(planet.x, planet.y, planet.radius + 8).fill({ color: PLANET_SURFACE, alpha: 0.98 });
    this.planet.circle(planet.x, planet.y, planet.radius - 12).fill({ color: PLANET_CORE, alpha: 1 });

    for (let i = 0; i < 96; i++) {
      const angle = (i / 96) * Math.PI * 2;
      const outer = planet.radius + 16;
      const inner = planet.radius - 5 - (i % 3) * 4;
      const x1 = planet.x + Math.cos(angle) * inner;
      const y1 = planet.y + Math.sin(angle) * inner;
      const x2 = planet.x + Math.cos(angle) * outer;
      const y2 = planet.y + Math.sin(angle) * outer;
      this.planet.moveTo(x1, y1).lineTo(x2, y2).stroke({ color: PLANET_RIM, width: 2, alpha: 0.38 });
    }
  }

  private drawFeatures(view: ViewModel): void {
    this.features.clear();
    for (const feature of view.features) {
      if (feature.kind === 'water' && feature.endDistance !== undefined) {
        drawSurfaceArc(this.features, feature.distance, feature.endDistance, 18, WATER_DARK, 0.48);
        drawSurfaceArc(this.features, feature.distance, feature.endDistance, 10, WATER, 0.92);
      } else if (feature.kind === 'forest') {
        this.drawForest(feature.distance);
      } else if (feature.kind === 'action' && !feature.unlocked) {
        this.drawActionStone(feature.x, feature.y, feature.active);
      }
    }
  }

  private drawActionStone(x: number, y: number, active: boolean): void {
    const frame = localFrame(x, y);
    const base = addLocal(x, y, frame, 0, 10);
    const top = addLocal(x, y, frame, 0, 45);
    const left = addLocal(x, y, frame, -12, 12);
    const right = addLocal(x, y, frame, 12, 12);
    this.features
      .poly([left.x, left.y, top.x, top.y, right.x, right.y, base.x, base.y])
      .fill({ color: STONE, alpha: active ? 0.96 : 0.72 })
      .stroke({ color: ACCENT, width: active ? 2.2 : 1.1, alpha: active ? 0.72 : 0.28 });
    if (active) this.features.circle(top.x, top.y, 8).fill({ color: ACCENT, alpha: 0.18 });
  }

  private drawForest(startDistance: number): void {
    for (let i = 0; i < 18; i++) {
      const distance = startDistance + i * 42;
      const p = pointAtDistance(distance, 0);
      const frame = localFrame(p.x, p.y);
      const root = addLocal(p.x, p.y, frame, 0, 4);
      const trunkTop = addLocal(p.x, p.y, frame, 0, 35 + (i % 3) * 5);
      const crown = addLocal(p.x, p.y, frame, 0, 61 + (i % 4) * 5);
      this.features.moveTo(root.x, root.y).lineTo(trunkTop.x, trunkTop.y).stroke({ color: TREE_DARK, width: 6, alpha: 0.9 });
      this.features.circle(crown.x, crown.y, 20 + (i % 3) * 4).fill({ color: TREE, alpha: 0.92 });
      this.features.circle(crown.x - frame.tangentX * 10, crown.y - frame.tangentY * 10, 13).fill({ color: TREE_DARK, alpha: 0.5 });
    }
  }

  private drawPlayer(view: ViewModel): void {
    this.player.clear();
    drawStickFigure(this.player, view);
  }

  private drawUi(view: ViewModel, layout: ReturnType<typeof computeScreenLayout>): void {
    this.ui.clear();
    this.statusText.text = '';
    if (view.camera.mode === 'deadzone') this.drawDeadzone(view, layout);
    if (view.discoveries.minimapUnlocked) this.drawMinimap(view, layout);
    this.drawStatus(view, layout);
    if (layout.compact) this.drawControls(layout);

    this.title.text = 'Planet Runner';
    this.title.alpha = 0.9;
    this.title.position.set(layout.x + 18, layout.y + 14);

    this.hud.text = `speed ${Math.round(view.diagnostics.speed)}   alt ${Math.round(view.diagnostics.altitude)}   zoom ${view.diagnostics.zoom.toFixed(2)}x   camera ${view.camera.mode}`;
    this.hud.alpha = 0.86;
    this.hud.position.set(layout.x + 18, layout.y + 34);

    this.detail.text = view.discoveries.minimapUnlocked
      ? 'A/D  walk    Space  jump    Wheel or Q/E  zoom    C  camera'
      : 'A/D  walk    Space  jump';
    this.detail.alpha = layout.compact ? 0 : 0.54;
    this.detail.position.set(layout.x + 18, layout.y + 53);
  }

  private drawStatus(view: ViewModel, layout: ReturnType<typeof computeScreenLayout>): void {
    const message = statusLabel(view.status.kind);
    if (message.length === 0) return;
    const x = layout.x + layout.w * 0.5;
    const y = layout.y + layout.h * 0.18;
    const w = Math.min(360, Math.max(160, message.length * 8 + 32));
    this.ui.roundRect(x - w * 0.5, y, w, 34, 8).fill({ color: PANEL, alpha: 0.42 });
    this.ui.roundRect(x - w * 0.5, y, w, 34, 8).stroke({ color: ACCENT, width: 1, alpha: 0.28 });
    this.statusText.text = message;
    this.statusText.alpha = Math.min(1, view.status.ticks / 24);
    this.statusText.position.set(x - this.statusText.width * 0.5, y + 9);
  }

  private drawDeadzone(view: ViewModel, layout: ReturnType<typeof computeScreenLayout>): void {
    const centerX = layout.x + layout.w * 0.5;
    const left = centerX - view.camera.deadzoneHalfWidthPx;
    const right = centerX + view.camera.deadzoneHalfWidthPx;
    const top = layout.y + layout.h * 0.18;
    const bottom = layout.y + layout.h * 0.74;
    this.ui.moveTo(left, top).lineTo(left, bottom).stroke({ color: ACCENT, width: 1, alpha: 0.16 });
    this.ui.moveTo(right, top).lineTo(right, bottom).stroke({ color: ACCENT, width: 1, alpha: 0.16 });
    this.ui
      .moveTo(left - 11, top)
      .lineTo(left, top)
      .lineTo(left, top + 11)
      .moveTo(left - 11, bottom)
      .lineTo(left, bottom)
      .lineTo(left, bottom - 11)
      .moveTo(right + 11, top)
      .lineTo(right, top)
      .lineTo(right, top + 11)
      .moveTo(right + 11, bottom)
      .lineTo(right, bottom)
      .lineTo(right, bottom - 11)
      .stroke({ color: ACCENT, width: 1.4, alpha: 0.24 });
  }

  private drawMinimap(view: ViewModel, layout: ReturnType<typeof computeScreenLayout>): void {
    const size = layout.compact ? 100 : 112;
    const x = layout.x + layout.w - size - 18;
    const y = layout.y + 18 + (layout.compact ? 44 : 0);
    const cx = x + size * 0.5;
    const cy = y + size * 0.5;
    const r = size * 0.36;
    const dotR = layout.compact ? 4 : 3.6;

    this.ui.roundRect(x, y, size, size, 8).fill({ color: PANEL, alpha: 0.42 });
    this.ui.roundRect(x, y, size, size, 8).stroke({ color: PANEL_STROKE, width: 1, alpha: 0.36 });
    this.ui.circle(cx, cy, r + 7).fill({ color: 0x83dcba, alpha: 0.05 });
    this.ui.circle(cx, cy, r).fill({ color: PLANET_CORE, alpha: 0.74 });
    this.ui.circle(cx, cy, r).stroke({ color: PLANET_RIM, width: 3, alpha: 0.82 });

    const px = cx + Math.cos(view.player.angle) * r;
    const py = cy + Math.sin(view.player.angle) * r;
    this.ui.moveTo(cx, cy).lineTo(px, py).stroke({ color: PLAYER_DOT, width: 1, alpha: 0.24 });
    this.ui.circle(px, py, dotR + 3).fill({ color: PLAYER_DOT, alpha: 0.18 });
    this.ui.circle(px, py, dotR).fill({ color: PLAYER_DOT, alpha: 0.98 });
  }

  private drawControls(layout: ReturnType<typeof computeScreenLayout>): void {
    const controls = computeTouchControls(layout);
    drawButton(this.ui, controls.left, '<', 0.48);
    drawButton(this.ui, controls.right, '>', 0.48);
    drawButton(this.ui, controls.jump, '^', 0.56);
    drawButton(this.ui, controls.zoomOut, '-', 0.42);
    drawButton(this.ui, controls.zoomIn, '+', 0.42);
  }
}

function drawButton(graphics: Graphics, rect: Rect, glyph: string, alpha: number): void {
  graphics.roundRect(rect.x, rect.y, rect.w, rect.h, 8).fill({ color: PANEL, alpha: alpha * 0.62 });
  graphics.roundRect(rect.x, rect.y, rect.w, rect.h, 8).stroke({ color: ACCENT, width: 1.2, alpha: alpha + 0.02 });
  const cx = rect.x + rect.w * 0.5;
  const cy = rect.y + rect.h * 0.5;
  if (glyph === '<' || glyph === '>') {
    const sign = glyph === '<' ? 1 : -1;
    graphics
      .moveTo(cx + sign * 10, cy - 12)
      .lineTo(cx - sign * 6, cy)
      .lineTo(cx + sign * 10, cy + 12)
      .stroke({ color: TEXT, width: 3.2, alpha: 0.7 });
    return;
  }
  if (glyph === '^') {
    graphics
      .moveTo(cx - 12, cy + 8)
      .lineTo(cx, cy - 10)
      .lineTo(cx + 12, cy + 8)
      .stroke({ color: TEXT, width: 3.2, alpha: 0.72 });
    return;
  }
  graphics.moveTo(cx - 9, cy).lineTo(cx + 9, cy).stroke({ color: TEXT, width: 2.8, alpha: 0.72 });
  if (glyph === '+') graphics.moveTo(cx, cy - 9).lineTo(cx, cy + 9).stroke({ color: TEXT, width: 2.8, alpha: 0.72 });
}

function makeText(fontSize: number, fill: number, fontWeight: TextStyleFontWeight): Text {
  return new Text({
    text: '',
    style: {
      fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSize,
      fontWeight,
      fill,
      letterSpacing: 0,
    },
  });
}

function drawSurfaceArc(graphics: Graphics, startDistance: number, endDistance: number, width: number, color: number, alpha: number): void {
  const steps = 24;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const distance = startDistance + (endDistance - startDistance) * t;
    const point = pointAtDistance(distance, 10);
    if (i === 0) graphics.moveTo(point.x, point.y);
    else graphics.lineTo(point.x, point.y);
  }
  graphics.stroke({ color, width, alpha, cap: 'round', join: 'round' });
}

function pointAtDistance(distance: number, altitude: number): { x: number; y: number } {
  const angle = PLANET.startAngle + distance / PLANET.radius;
  const radius = PLANET.radius + altitude;
  return {
    x: PLANET.centerX + Math.cos(angle) * radius,
    y: PLANET.centerY + Math.sin(angle) * radius,
  };
}

function localFrame(x: number, y: number): { tangentX: number; tangentY: number; upX: number; upY: number } {
  const upX = x - PLANET.centerX;
  const upY = y - PLANET.centerY;
  const len = Math.max(1, Math.hypot(upX, upY));
  const nx = upX / len;
  const ny = upY / len;
  return {
    upX: nx,
    upY: ny,
    tangentX: -ny,
    tangentY: nx,
  };
}

function addLocal(
  x: number,
  y: number,
  frame: ReturnType<typeof localFrame>,
  tangent: number,
  up: number,
): { x: number; y: number } {
  return {
    x: x + frame.tangentX * tangent + frame.upX * up,
    y: y + frame.tangentY * tangent + frame.upY * up,
  };
}

function statusLabel(kind: ViewModel['status']['kind']): string {
  switch (kind) {
    case 'left-action':
      return 'A quiet marker waits here.';
    case 'minimap-unlocked':
      return 'You notice the shape of home.';
    case 'water':
      return 'Swimming uses stamina.';
    case 'returned-home':
      return 'The water carries you home.';
    case 'forest-blocked':
      return 'The forest is too dense for now.';
    default:
      return '';
  }
}
